import React, { useState, useMemo } from 'react';
import { Modal, Space, Button, Typography, Card, Row, Col } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { DataTable } from '../../ui';
import YandexMap from './YandexMap';
import { CargoLocation, useGetRouteLocationsQuery, useGetCargoLocationsQuery } from '../../store/api/locationsApi';
import { formatUserDisplayName } from '../../utils/userDisplayName';

const { Text, Title } = Typography;

interface LocationHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  routeId?: number;
  cargoId?: number;
  title?: string;
}

const LocationHistoryModal: React.FC<LocationHistoryModalProps> = ({
  visible,
  onClose,
  routeId,
  cargoId,
  title = 'История местоположений'
}) => {
  const [selectedLocation, setSelectedLocation] = useState<CargoLocation | null>(null);

  // Получаем историю местоположений
  const { data: routeLocations, isLoading: routeLoading } = useGetRouteLocationsQuery(
    routeId!,
    { skip: !routeId }
  );

  const { data: cargoLocations, isLoading: cargoLoading } = useGetCargoLocationsQuery(
    cargoId!,
    { skip: !cargoId }
  );

  // Используем данные в зависимости от того, что передано
  const locations = routeId ? routeLocations : cargoLocations;
  const isLoading = routeId ? routeLoading : cargoLoading;

  // Маркеры для карты
  const markers = useMemo(() => {
    if (!locations) return [];
    
    return locations.map((location, index) => ({
      id: location.id.toString(),
      position: [Number(location.latitude), Number(location.longitude)] as [number, number],
      title: `Местоположение ${index + 1}`,
      description: location.address || `Координаты: ${location.latitude}, ${location.longitude}`,
      color: index === 0 ? 'red' : 'blue' // Последнее местоположение красным
    }));
  }, [locations]);

  // Центр карты - последнее местоположение или по умолчанию
  const mapCenter = useMemo(() => {
    if (locations && locations.length > 0) {
      const lastLocation = locations[0]; // Сортировка по дате desc
      return [Number(lastLocation.latitude), Number(lastLocation.longitude)] as [number, number];
    }
    return [55.7558, 37.6176] as [number, number]; // Москва по умолчанию
  }, [locations]);

  const columns: ColumnsType<CargoLocation> = [
    {
      title: '№',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 200,
      render: (_, record) => {
        if (!record.route) return '—';
        
        const route = record.route;
        const routeText = `${route.loadingAddress || '—'} → ${route.unloadingAddress || '—'}`;
        return (
          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
              #{record.route.id}
            </div>
            <div style={{ color: 'var(--text)' }}>
              {routeText.length > 30 ? (
                <>
                  <div>{routeText.substring(0, 30)}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{routeText.substring(30)}</div>
                </>
              ) : (
                routeText
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Координаты',
      key: 'coordinates',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {typeof record.latitude === 'number' ? record.latitude.toFixed(6) : record.latitude}, {typeof record.longitude === 'number' ? record.longitude.toFixed(6) : record.longitude}
          </div>
          {record.address && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              {record.address}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Дата записи',
      key: 'recordedAt',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{dayjs(record.recordedAt).format('DD.MM.YYYY')}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {dayjs(record.recordedAt).format('HH:mm:ss')}
          </div>
        </div>
      )
    },
    {
      title: 'Фактические даты',
      key: 'actualDates',
      width: 200,
      render: (_, record) => {
        if (!record.route) return '—';
        const route = record.route;
        
        return (
          <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
            {route.actualLoadingDate && (
              <div>
                <span style={{ color: '#52c41a' }}>Погрузка:</span> {dayjs(route.actualLoadingDate).format('DD.MM.YYYY')}
              </div>
            )}
            {route.actualUnloadingDate && (
              <div>
                <span style={{ color: '#fa8c16' }}>Выгрузка:</span> {dayjs(route.actualUnloadingDate).format('DD.MM.YYYY')}
              </div>
            )}
            {!route.actualLoadingDate && !route.actualUnloadingDate && (
              <div style={{ color: '#999' }}>Нет данных</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Записал',
      key: 'user',
      width: 100,
      render: (_, record) => formatUserDisplayName(record.user)
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setSelectedLocation(record)}
            title="Показать на карте"
          />
        </Space>
      )
    }
  ];

  const handleLocationSelect = (location: CargoLocation) => {
    setSelectedLocation(location);
  };

  const handleMapMarkerClick = (markerId: string | number) => {
    const location = locations?.find(loc => loc.id.toString() === markerId.toString());
    if (location) {
      setSelectedLocation(location);
    }
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1400}
      destroyOnHidden
      maskClosable={false}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Card title="История местоположений" size="small">
            <DataTable
              columns={columns}
              dataSource={locations || []}
              loading={isLoading}
              rowKey="id"
              size="small"
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showTotal: (total) => `Всего: ${total}`
              }}
              onRow={(record) => ({
                onClick: () => handleLocationSelect(record),
                style: { cursor: 'pointer' }
              })}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Карта местоположений" size="small">
            <YandexMap
              center={mapCenter}
              zoom={10}
              markers={markers}
              onMarkerClick={handleMapMarkerClick}
              height={400}
            />
            {selectedLocation && (
              <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <Title level={5}>Выбранное местоположение:</Title>
                
                {selectedLocation.route && (
                  <>
                    <div style={{ marginBottom: 12, padding: 8, backgroundColor: '#e6f7ff', borderRadius: 4 }}>
                      <Text strong style={{ color: '#1890ff' }}>Информация о маршруте:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Text strong>№{selectedLocation.route.id}</Text>
                      </div>
                      <div style={{ fontSize: '12px', marginTop: 2 }}>
                        <div>Погрузка: {selectedLocation.route.loadingAddress || '—'}</div>
                        <div>Выгрузка: {selectedLocation.route.unloadingAddress || '—'}</div>
                      </div>
                    </div>
                  </>
                )}
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Координаты:</Text> {typeof selectedLocation.latitude === 'number' ? selectedLocation.latitude.toFixed(6) : selectedLocation.latitude}, {typeof selectedLocation.longitude === 'number' ? selectedLocation.longitude.toFixed(6) : selectedLocation.longitude}
                </div>
                
                {selectedLocation.address && (
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Адрес:</Text> {selectedLocation.address}
                  </div>
                )}
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Дата записи:</Text> {dayjs(selectedLocation.recordedAt).format('DD.MM.YYYY HH:mm:ss')}
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Записал:</Text> {formatUserDisplayName(selectedLocation.user)}
                </div>
                
                {selectedLocation.route && (
                  <>
                    <div style={{ marginTop: 12, padding: 8, backgroundColor: '#f6ffed', borderRadius: 4 }}>
                      <Text strong style={{ color: '#52c41a' }}>Фактические даты:</Text>
                      <div style={{ fontSize: '12px', marginTop: 4 }}>
                        {(selectedLocation.route.actualLoadingDate) && (
                          <div>Погрузка: {dayjs(selectedLocation.route.actualLoadingDate).format('DD.MM.YYYY')}</div>
                        )}
                        {(selectedLocation.route.actualUnloadingDate) && (
                          <div>Выгрузка: {dayjs(selectedLocation.route.actualUnloadingDate).format('DD.MM.YYYY')}</div>
                        )}
                        {!(selectedLocation.route.actualLoadingDate) && !(selectedLocation.route.actualUnloadingDate) && (
                          <div style={{ color: '#999' }}>Нет данных о фактических датах</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default LocationHistoryModal; 